import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Button, Card, Col, Modal, Row,
} from 'react-bootstrap';
import { can } from '../../../common/Util';
import LargeUserPill from '../../../common/components/LargeUserPill';
import SalaryResource from '../../../common/resource/SalaryResource';
import { Icon, LoadingAnimation } from '../../../common/components/Utilities';
import UserPill from '../../../common/components/UserPill';

const formatters = {
  GBP: new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }),

  EUR: new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }),

  HUF: new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }),
};

const formatter = formatters.GBP;

function OctoberReview() {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(true);
  const [data, setData] = useState({});

  const load = async () => {
    setLoading(true);
    setData(await SalaryResource.review({ filter: (filter ? 'october' : null) }));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [filter]);

  return (
    <div style={{ position: 'relative', minHeight: '400px' }}>
      <LoadingAnimation loading={loading} />
      <Row>
        {can('leadership') && (
          <>
            <Col md={6}>
              <div className="alert alert-info mb-4">
                <h4>
                  <i className="fa fad fa-info-circle" />
                  {' '}
                  Proposal summary
                </h4>

                <table className="table mt-4">
                  <thead>
                    <tr>
                      <th />
                      <th>People</th>
                      <th>Amount</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && (
                      <>
                        <SummaryRow
                          title="Current salary total:"
                          description="Total based on current salaries."
                          data={data.before}
                        />

                        <SummaryRow
                          title="Proposed increases:"
                          description="Sum of increases proposed below."
                          data={data.october}
                        />

                        <SummaryRow
                          title="Leaver salaries:"
                          description="Sum of salaries of people working their notice."
                          data={data.leavers}
                        />

                        <SummaryRow
                          title="New salary total:"
                          description="Salary total after increases, less leaver salaris."
                          data={data.after}
                          isTotal
                        />
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <div className="form-group form-check mb-0">
                    <input
                      id="enable-filter"
                      className="form-check-input"
                      type="checkbox"
                      checked={filter}
                      onChange={(event) => setFilter(event.target.checked)}
                    />

                    <label className="form-check-label" htmlFor="enable-filter">
                      Hide employees without proposed changes.
                    </label>
                  </div>

                  <a href="/resource/salary/reviews?filter=october&amp;download=1" className="btn btn-outline-success mt-2">
                    <Icon icon="cloud-download" append="mr-2" />
                    Download
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Card>
        <table className="table table-striped mb-0">
          <thead>
            <tr>
              <th width="1" />
              <th width="1" />
              <th>Current</th>
              <th>October (Approved)</th>
              <th>October (Proposed)</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.users.map((user) => (<OctoberUserRow key={user.id} user={user} />))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function AprilReview() {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(true);
  const [data, setData] = useState({});

  const load = async () => {
    setLoading(true);
    setData(await SalaryResource.review({ filter: (filter ? 'april' : null) }));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [filter]);

  return (
    <div style={{ position: 'relative', minHeight: '400px' }}>
      <LoadingAnimation loading={loading} />
      <Row>
        {(can('leadership') || can('userAdmin')) && (
          <>
            <Col md={6}>
              <div className="alert alert-info mb-4">
                <h4>
                  <i className="fa fad fa-info-circle" />
                  {' '}
                  Proposal summary
                </h4>

                <table className="table mt-4">
                  <thead>
                    <tr>
                      <th />
                      <th>People</th>
                      <th>Amount</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && (
                      <>
                        <SummaryRow
                          title="Current salary total:"
                          description="Total based on current salaries."
                          data={data.before}
                        />

                        <SummaryRow
                          title="April increases:"
                          description="Sum of increases proposed for April."
                          data={data.april}
                        />

                        <SummaryRow
                          title="October increases:"
                          description="Sum of increases proposed for October."
                          data={data.october}
                        />

                        <SummaryRow
                          title="Leaver salaries:"
                          description="Sum of salaries of people working their notice."
                          data={data.leavers}
                        />

                        <SummaryRow
                          title="Active vacancies:"
                          description="Sum of target salaries for active vacancies."
                          data={data.vacancies}
                        />

                        <SummaryRow
                          title="New salary total:"
                          description="Salary total after increases, less leaver salaris."
                          data={data.after}
                          isTotal
                        />
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <div className="form-group form-check mb-0">
                    <input
                      id="enable-filter"
                      className="form-check-input"
                      type="checkbox"
                      checked={filter}
                      onChange={(event) => setFilter(event.target.checked)}
                    />

                    <label className="form-check-label" htmlFor="enable-filter">
                      Hide employees without proposed changes.
                    </label>
                  </div>

                  <a href="/resource/salary/reviews?filter=april&amp;download=1" className="btn btn-outline-success mt-2">
                    <Icon icon="cloud-download" append="mr-2" />
                    Download
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Card>
        <table className="table table-striped mb-0">
          <thead>
            <tr>
              <th width="1" />
              <th width="1" />
              <th>Current</th>
              <th>April</th>
              <th>October</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.users.map((user) => (<AprilUserRow key={user.id} user={user} />))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SalaryHistoryCell({ user }) {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <td style={{ verticalAlign: 'top' }}>
      <Button variant="default" onClick={() => setShowHistory(true)}>
        <Icon icon="timeline-arrow" variant="fad" />
      </Button>

      {showHistory && (
      <SalaryHistory user={user} show={showHistory} setShow={setShowHistory} />
      )}
    </td>
  );
}

function OctoberUserRow({ user }) {
  const octoberProposed = user.october_proposed_salary;

  const [saving, setSaving] = useState(false);
  const [proposed, setProposed] = useState(octoberProposed ? octoberProposed.amount : null);
  const [reason, setReason] = useState(octoberProposed ? octoberProposed.reason : null);

  const reasonStyle = {
    fontSize: '0.8rem',
  };

  if (saving) {
    reasonStyle.background = 'rgba(255, 229, 100, 0.2)';
  }

  const salaryChanged = async (amount) => {
    setSaving(true);

    try {
      setProposed(amount);
      await SalaryResource.update(user.october_proposed_salary.id, { amount });
      setSaving(false);
    } catch (e) {
      alert('Failed to save salary change.');
    }
  };

  const saveReason = async () => {
    setSaving(true);

    try {
      await SalaryResource.update(user.october_proposed_salary.id, { reason });
      setSaving(false);
    } catch (e) {
      alert('Failed to save salary reason.');
    }
  };

  return (
    <tr>
      <td style={{ verticalAlign: 'top' }}>
        <LargeUserPill user={user} />
        <div className="small text-muted">
          <strong>
            Start date:
          </strong>
          {' '}
          {moment(user.start_date).format('D MMMM YYYY')}
        </div>
      </td>

      <SalaryHistoryCell user={user} />

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {user.salary && (
          <>
            <SalaryField salary={user.salary} disabled />
            <div className="small text-muted">
              {user.salary.currency !== 'GBP' && (
                <span className="mr-2">
                  Approx.
                  {' '}
                  {formatter.format(user.salary.localised.gbp)}
                </span>
              )}

              Since
              {' '}
              {moment(user.salary.effective_from).format('D MMMM YYYY')}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {user.october_salary && (
          <>
            <SalaryField salary={user.october_salary} disabled />
            <div className="small">
              {user.october_salary.currency !== 'GBP' && (
                <span className="text-muted mr-2">
                  Approx.
                  {' '}
                  {formatter.format(user.october_salary.localised.gbp)}
                </span>
              )}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {user.october_proposed_salary && (
          <>
            <SalaryField
              salary={user.october_proposed_salary}
              amount={proposed}
              onChange={(event) => salaryChanged(event.target.value)}
              saving={saving}
              disabled={saving}
            />

            <div className="small">
              {user.october_proposed_salary.currency !== 'GBP' && (
                <span className="text-muted mr-2">
                  Approx.
                  {' '}
                  {formatter.format(user.october_proposed_salary.localised.gbp)}
                </span>
              )}

              {user.october_proposed_salary.currency === user.salary.currency && (
                <span className="text-success">
                  +
                  {' '}
                  {formatters[user.salary.currency].format(proposed - user.salary.amount)}
                  {' ('}
                  {((100 / user.salary.amount) * proposed - 100).toFixed(1)}
                  %)
                </span>
              )}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '300px' }}>
        {user.october_proposed_salary !== null && (
          <>
            <div>
              <textarea
                className="form-control"
                style={reasonStyle}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onBlur={() => saveReason()}
              />
            </div>

            <div>
              {user.retention_status !== null && (
                <RetentionBadge status={user.retention_status} />
              )}

              {user.flag_flight === 1 && (
                <span className="badge badge-danger mr-1">
                  <Icon icon="flag" append="mr-1" />
                  Flight Risk
                </span>
              )}

              {user.flag_salary === 1 && (
                <span className="badge badge-danger mr-1">
                  <Icon icon="flag" append="mr-1" />
                  Salary Concern
                </span>
              )}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

function AprilUserRow({ user }) {
  const [aprilProposed, setAprilProposed] = useState(user.april_proposed_salary);
  const [octoberProposed, setOctoberProposed] = useState(user.october_proposed_salary);

  const [saving, setSaving] = useState(false);
  const [april, setApril] = useState(aprilProposed ? aprilProposed.amount : null);
  const [october, setOctober] = useState(octoberProposed ? octoberProposed.amount : null);
  const [reason, setReason] = useState(aprilProposed ? aprilProposed.reason : null);

  const reasonStyle = {
    fontSize: '0.8rem',
  };

  if (saving) {
    reasonStyle.background = 'rgba(255, 229, 100, 0.2)';
  }

  const createApril = async () => {
    setSaving(true);

    try {
      const newSalary = await SalaryResource.create({
        user_id: user.id,
        month: 4,
      });

      setAprilProposed(newSalary);
      setApril(newSalary.amount);
    } catch (e) {
      alert('Failed to save salary change.');
    }
  };

  const createOctober = async () => {
    setSaving(true);

    try {
      const newSalary = await SalaryResource.create({
        user_id: user.id,
        month: 10,
      });

      setOctoberProposed(newSalary);
      setOctober(newSalary.amount);
    } catch (e) {
      alert('Failed to save salary change.');
    }
  };

  const aprilChanged = async (amount) => {
    setSaving(true);

    try {
      setApril(amount);
      setAprilProposed(await SalaryResource.update(user.april_proposed_salary.id, { amount }));
      setSaving(false);
    } catch (e) {
      alert('Failed to save salary change.');
    }
  };

  const octoberChanged = async (amount) => {
    setSaving(true);

    try {
      setOctober(amount);
      setOctoberProposed(await SalaryResource.update(user.october_proposed_salary.id, { amount }));
      setSaving(false);

      const newApril = user.salary.amount + ((amount - user.salary.amount) / 2);
      aprilChanged(newApril);
    } catch (e) {
      alert('Failed to save salary change.');
    }
  };

  const saveReason = async () => {
    setSaving(true);

    try {
      await SalaryResource.update(user.april_proposed_salary.id, { reason });
      setSaving(false);
    } catch (e) {
      alert('Failed to save salary reason.');
    }
  };

  return (
    <tr>
      <td style={{ verticalAlign: 'top' }}>
        <LargeUserPill user={user} />
        <div className="small text-muted">
          <strong>
            Start date:
          </strong>
          {' '}
          {moment(user.start_date).format('D MMMM YYYY')}
        </div>
      </td>

      <SalaryHistoryCell user={user} />

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {user.salary && (
          <>
            <SalaryField salary={user.salary} disabled />
            <div className="small text-muted">
              {user.salary.currency !== 'GBP' && (
                <span className="mr-2">
                  Approx.
                  {' '}
                  {formatter.format(user.salary.localised.gbp)}
                </span>
              )}
              {user.salary.gbp_amount !== user.salary.full_time_gbp_amount && (
                <span className="mr-2">
                  FT Approx.
                  {' '}
                  {formatter.format(user.salary.localised.full_time_gbp)}
                </span>
              )}

              Since
              {' '}
              {moment(user.salary.effective_from).format('D MMMM YYYY')}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {aprilProposed === null && (
          <Icon icon="plus-circle" append="text-success" onClick={createApril} />
        )}

        {aprilProposed !== null && (
          <>
            <SalaryField
              salary={aprilProposed}
              amount={april}
              onChange={(event) => aprilChanged(event.target.value)}
              saving={saving}
              disabled={saving}
            />

            <div className="small">
              {aprilProposed.currency !== 'GBP' && (
                <span className="text-muted mr-2">
                  Approx.
                  {' '}
                  {formatter.format(aprilProposed.localised.gbp)}
                </span>
              )}
              {aprilProposed.gbp_amount !== aprilProposed.full_time_gbp_amount && (
                <span className="mr-2">
                  FT Approx.
                  {' '}
                  {formatter.format(aprilProposed.localised.full_time_gbp)}
                </span>
              )}

              {aprilProposed.currency === user.salary.currency && (
                <span className="text-success">
                  +
                  {' '}
                  {formatters[user.salary.currency].format(april - user.salary.amount)}
                  {' ('}
                  {((100 / user.salary.amount) * april - 100).toFixed(1)}
                  %)
                </span>
              )}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '200px' }}>
        {octoberProposed === null && (
          <Icon icon="plus-circle" append="text-success" onClick={createOctober} />
        )}

        {octoberProposed !== null && (
          <>
            <SalaryField
              salary={octoberProposed}
              amount={october}
              onChange={(event) => octoberChanged(event.target.value)}
              saving={saving}
              disabled={saving}
            />

            <div className="small">
              {octoberProposed.currency !== 'GBP' && (
                <span className="text-muted mr-2">
                  Approx.
                  {' '}
                  {formatter.format(octoberProposed.localised.gbp)}
                </span>
              )}
              {octoberProposed.gbp_amount !== octoberProposed.full_time_gbp_amount && (
                <span className="mr-2">
                  FT Approx.
                  {' '}
                  {formatter.format(octoberProposed.localised.full_time_gbp)}
                </span>
              )}

              {octoberProposed.currency === user.salary.currency && (
                <span className="text-success">
                  +
                  {' '}
                  {formatters[user.salary.currency].format(october - user.salary.amount)}
                  {' ('}
                  {((100 / user.salary.amount) * october - 100).toFixed(1)}
                  %)
                </span>
              )}
            </div>
          </>
        )}
      </td>

      <td style={{ verticalAlign: 'top', maxWidth: '300px' }}>
        {user.october_proposed_salary !== null && (
          <>
            <div>
              <textarea
                className="form-control"
                style={reasonStyle}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onBlur={() => saveReason()}
              />
            </div>

            <div>
              {user.retention_status !== null && (
                <RetentionBadge status={user.retention_status} />
              )}

              {user.flag_flight === 1 && (
                <span className="badge badge-danger mr-1">
                  <Icon icon="flag" append="mr-1" />
                  Flight Risk
                </span>
              )}

              {user.flag_salary === 1 && (
                <span className="badge badge-danger mr-1">
                  <Icon icon="flag" append="mr-1" />
                  Salary Concern
                </span>
              )}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

function SalaryHistory({ user, show, setShow }) {
  return (
    <Modal size="md" show={show} onHide={() => setShow(null)}>
      <Modal.Header closeButton>
        <Modal.Title>
          Salary History for
          {' '}
          {user.name}
        </Modal.Title>
      </Modal.Header>

      <table className="table mb-0">
        <thead>
          <tr>
            <th>Salary</th>
            <th>Effective From</th>
          </tr>
        </thead>
        <tbody>
          {user.salaries.map((salary) => salary.approved === 1 && (
            <tr key={`salary-${salary.id}`} className={salary.id === user.salary.id ? 'text-success' : ''}>
              <td>
                {formatters[salary.currency].format(salary.amount)}
              </td>
              <td>
                {moment(salary.effective_from).format('D MMMM YYYY')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

function RetentionBadge({ status }) {
  switch (status) {
    case 'm':
      return (
        <span className="badge badge-success mr-1">
          <Icon icon="exclamation-triangle" append="mr-1" />
          Must Retain
        </span>
      );

    case 's':
      return (
        <span className="badge badge-success mr-1">
          <Icon icon="check" append="mr-1" />
          Should Retain
        </span>
      );

    case 'w':
      return (<span className="badge badge-info mr-1">Would Retain</span>);

    case 'c':
      return (<span className="badge badge-warning mr-1">Could Retain</span>);

    case 'n':
      return (<span className="badge badge-danger mr-1">No Retain</span>);

    default:
      return null;
  }
}

function SummaryRow({
  title,
  description,
  data,
  isTotal = false,
}) {
  let className;
  let symbol;

  if (data.diff > 0) {
    className = 'text-success';
    symbol = '+ ';
  } else if (data.diff < 0) {
    className = 'text-danger';
    symbol = '- ';
  }

  return (
    <tr>
      <th>
        {title}
        <div className="small text-muted mt-0">
          {description}
        </div>
      </th>

      <td style={{ verticalAlign: 'middle' }}><small>{data.count}</small></td>

      <td style={{ verticalAlign: 'middle' }} className={isTotal ? '' : className}>
        {isTotal ? '' : symbol}
        {formatter.format(data.total)}
      </td>

      <td style={{ verticalAlign: 'middle' }} className={className}>
        {data.diff && (
          <span className="small">
            {symbol}
            {Math.abs(data.diff).toFixed(2)}
            %
          </span>
        )}
      </td>
    </tr>
  );
}

function SalaryField({
  salary,
  amount = null,
  saving = false,
  disabled = false,
  onChange,
}) {
  const salaryAmount = salary ? salary.amount : null;
  const useAmount = amount ?? salaryAmount;
  const [value, setValue] = useState(useAmount);
  const style = {};

  if (value !== useAmount || saving) {
    style.background = 'rgba(255, 229, 100, 0.2)';
  }

  return (
    <div className="input-group">
      {(salary && salary.localised && salary.localised.prefix) && (
        <div className="input-group-prepend">
          <span className="input-group-text">{salary.localised.prefix}</span>
        </div>
      )}

      <input
        disabled={disabled || saving}
        className="form-control salary-amount"
        style={style}
        name="amount"
        value={value}
        type="number"
        step={1}
        onChange={(event) => setValue(event.target.value)}
        onBlur={onChange}
      />

      {(salary && salary.localised && salary.localised.suffix) && (
        <div className="input-group-append">
          <span className="input-group-text">{salary.localised.suffix}</span>
        </div>
      )}
    </div>
  );
}

function SalarySummary() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const load = async () => {
    setLoading(true);
    const response = await SalaryResource.get();
    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <>
      <LoadingAnimation loading={loading} />
      {data && (
      <table className="table mb-0">
        <thead>
          <tr>
            <th>Name</th>
            <th>Level</th>
            <th>Salary</th>
            <th>FT GBP</th>
            <th title="How far through the salary band for this level is their current salary">Percentile</th>
            <th>Proposed (Unconfirmed)</th>
            <th>History</th>
          </tr>
        </thead>
        <tbody>
          {data.map((user) => (
            <tr key={`user-${user.id}`}>
              <td><UserPill user={user} /></td>
              <td>{user.job.level}</td>
              <td>
                {formatters[user.salary.currency].format(user.salary.amount)}
              </td>
              <td>
                {formatters.GBP.format(user.salary.full_time_gbp_amount)}
              </td>
              <td>
                {user.salary_band_percentile && `${user.salary_band_percentile} %`}
              </td>
              <td>
                {user.proposed_salary
                    && formatters[user.proposed_salary.currency]
                      .format(user.proposed_salary.amount)}
              </td>
              <SalaryHistoryCell user={user} />
            </tr>
          ))}
        </tbody>
      </table>
      )}
    </>
  );
}

export default {
  April: AprilReview,
  October: OctoberReview,
  Summary: SalarySummary,
};
